/**
 * Where the dates on screen actually came from.
 *
 * Todoist has two dates and Project Hub mirrors both — its DATE into `start`,
 * its DEADLINE into `due`. When a date column looks wrong there are only two
 * possible explanations, and they need opposite fixes:
 *
 *   - Todoist really does set that field, and the mapping is wrong; or
 *   - the mirror holds a value Todoist no longer sends, left behind by an
 *     older version of the sync.
 *
 * Guessing between those from the UI is impossible, so this counts both sides
 * and prints them next to each other.
 */

import { useState } from "react";

import { pb } from "@/lib/pocketbase";
import { todoistGet, todoistUnavailableReason } from "@/lib/todoistClient";
import type { Item } from "@/lib/types";

interface WireTask {
  id: string;
  due: { date: string } | null;
  deadline: { date: string } | null;
}

interface Counts {
  tasks: number;
  withDate: number;
  withDeadline: number;
  mirrored: number;
  mirroredStart: number;
  mirroredDue: number;
}

type State =
  | { kind: "idle" }
  | { kind: "checking" }
  | { kind: "done"; counts: Counts }
  | { kind: "error"; message: string };

export function DateCheck() {
  const [state, setState] = useState<State>({ kind: "idle" });
  const blocked = todoistUnavailableReason();

  async function check() {
    setState({ kind: "checking" });
    try {
      const [tasks, items] = await Promise.all([
        todoistGet<WireTask[]>("tasks"),
        pb.collection("items").getFullList<Item>({ filter: 'todoistId != ""', fields: "start,due" }),
      ]);
      setState({
        kind: "done",
        counts: {
          tasks: tasks.length,
          withDate: tasks.filter((t) => t.due?.date).length,
          withDeadline: tasks.filter((t) => t.deadline?.date).length,
          mirrored: items.length,
          mirroredStart: items.filter((i) => i.start).length,
          mirroredDue: items.filter((i) => i.due).length,
        },
      });
    } catch (err) {
      setState({ kind: "error", message: err instanceof Error ? err.message : String(err) });
    }
  }

  const c = state.kind === "done" ? state.counts : null;
  // The mirror claiming more deadlines than Todoist sends is the tell: those
  // are left over, not incoming.
  const stale = c !== null && c.mirroredDue > c.withDeadline;

  return (
    <div style={{ display: "flex", alignItems: "flex-start", gap: 10, flexWrap: "wrap", justifyContent: "flex-end" }}>
      {c && (
        <div style={{ fontSize: 12, color: "var(--ink3)", textAlign: "right", lineHeight: 1.6 }}>
          <div className="mono">
            Todoist: {c.tasks} tasks · {c.withDate} dated · <b>{c.withDeadline} with a deadline</b>
          </div>
          <div className="mono">
            Here: {c.mirrored} mirrored · {c.mirroredStart} with a start · <b>{c.mirroredDue} with a deadline</b>
          </div>
          {stale && (
            <div style={{ color: "var(--warn)", maxWidth: 340 }}>
              {c.mirroredDue - c.withDeadline} deadline{c.mirroredDue - c.withDeadline === 1 ? "" : "s"} here that
              Todoist does not send — left by an older sync. Run the mirror once to clear them.
            </div>
          )}
          {!stale && c.mirroredDue === c.withDeadline && (
            <div style={{ color: "var(--good)" }}>Both sides agree.</div>
          )}
        </div>
      )}
      {state.kind === "error" && (
        <span style={{ fontSize: 12.5, color: "var(--bad)", maxWidth: 340, textAlign: "right" }}>{state.message}</span>
      )}
      <button
        type="button"
        onClick={() => void check()}
        disabled={state.kind === "checking" || blocked !== null}
        title={blocked ?? undefined}
        style={{
          flex: "none",
          padding: "7px 14px", borderRadius: 999, border: "1px solid var(--line2)",
          background: "var(--surface)", fontSize: 12.5,
          color: blocked ? "var(--muted3)" : "var(--ink2)",
          cursor: blocked ? "not-allowed" : "pointer",
        }}
      >
        {state.kind === "checking" ? "Counting…" : "Count dates"}
      </button>
    </div>
  );
}
